package com.evoapps.evoshift.ui.user

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.widgets.IconButtonWithTooltip
import com.evoapps.evoshift.ui.theme.animateItemIfMotion
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.grid.GridItemSpan
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowLeft
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.BeachAccess
import androidx.compose.material.icons.filled.Download
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.LeaveAllowance
import com.evoapps.evoshift.domain.model.TeamLeaveBalance
import com.evoapps.evoshift.ui.Cream
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.adaptive.AdaptiveCardGrid
import com.evoapps.evoshift.ui.adaptive.LocalWindowWidthClass
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.Gate
import com.evoapps.evoshift.viewmodels.LeaveBalancesViewModel
import com.evoapps.evoshift.viewmodels.shows
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.EvoTopBar
import com.evoapps.evoshift.widgets.LoadingOverlay
import com.evoapps.evoshift.widgets.RegularCard
import com.evoapps.evoshift.widgets.TopBarAction
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.leave_allowance_carry_over
import evoshift.composeapp.generated.resources.leave_allowance_entitlement
import evoshift.composeapp.generated.resources.leave_allowance_entitlement_hint
import evoshift.composeapp.generated.resources.leave_allowance_entitlement_hint_unknown
import evoshift.composeapp.generated.resources.leave_allowance_invalid
import evoshift.composeapp.generated.resources.leave_allowance_title
import evoshift.composeapp.generated.resources.leave_balance_left
import evoshift.composeapp.generated.resources.leave_balance_unknown
import evoshift.composeapp.generated.resources.leave_balances_edit
import evoshift.composeapp.generated.resources.leave_balances_empty
import evoshift.composeapp.generated.resources.leave_balances_export
import evoshift.composeapp.generated.resources.leave_balances_title
import evoshift.composeapp.generated.resources.save
import evoshift.composeapp.generated.resources.top_bar_next_year
import evoshift.composeapp.generated.resources.top_bar_previous_year
import org.jetbrains.compose.resources.stringResource

/**
 * SH-113: the team's annual leave balances (Team → Leave balances): a card per person with the
 * days left of the entitlement and carry-over, the figures behind them and, for managers who may
 * change the workplace, "Change leave" (entitlement and carry-over). Export CSV in the top bar.
 */
@Composable
fun LeaveBalancesScreen(
    viewModel: LeaveBalancesViewModel,
    onNavigateBack: () -> Unit
) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    var editingUserId by rememberSaveable { mutableStateOf<String?>(null) }

    val messageText = state.message?.asString()
    LaunchedEffect(messageText) {
        messageText?.let {
            snackbarHostState.showSnackbar(it)
            viewModel.messageShown()
        }
    }

    Scaffold(
        containerColor = Cream,
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            EvoTopBar(
                title = stringResource(Res.string.leave_balances_title),
                onBack = onNavigateBack,
                showWorkplace = true,
                actions = buildList {
                    if (state.rows.isNotEmpty() && state.capabilities.shows(Gate.LEAVE_BALANCES)) {
                        add(TopBarAction(stringResource(Res.string.leave_balances_export), Icons.Default.Download, { viewModel.export() }))
                    }
                }
            )
        }
    ) { padding ->
        Box(Modifier.fillMaxSize().padding(padding)) {
            AdaptiveCardGrid(
                modifier = Modifier.fillMaxSize(),
                spacing = 16.dp,
                contentPadding = PaddingValues(16.dp),
                maxWidth = LocalWindowWidthClass.current.wideContentMaxWidth
            ) {
                item(span = { GridItemSpan(maxLineSpan) }) {
                    YearSwitcher(state.year, onChange = viewModel::changeYear)
                }
                if (state.rows.isEmpty() && !state.isLoading) {
                    item(span = { GridItemSpan(maxLineSpan) }) {
                        Text(stringResource(Res.string.leave_balances_empty), color = DeepNavy.copy(alpha = 0.5f))
                    }
                }
                items(state.rows, key = { it.userId }) { row ->
                    Box(Modifier.animateItemIfMotion(this), propagateMinConstraints = true) {
                        PersonLeaveCard(row, canEdit = state.canEdit, onEdit = { editingUserId = row.userId })
                    }
                }
            }
            LoadingOverlay(isLoading = state.isLoading && state.rows.isEmpty())
        }
    }

    state.rows.firstOrNull { it.userId == editingUserId }?.let { row ->
        LeaveAllowanceDialog(
            row = row,
            onDismiss = { editingUserId = null },
            onSave = { entitlement, carryOver ->
                viewModel.saveAllowance(row.userId, entitlement, carryOver)
                editingUserId = null
            }
        )
    }
}

@Composable
private fun YearSwitcher(year: Int, onChange: (Int) -> Unit) {
    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.Center) {
        IconButtonWithTooltip(onClick = { onChange(-1) }, tooltip = stringResource(Res.string.top_bar_previous_year)) {
            Icon(Icons.AutoMirrored.Filled.KeyboardArrowLeft, contentDescription = stringResource(Res.string.top_bar_previous_year), tint = DeepNavy)
        }
        Text(year.toString(), style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold, color = DeepNavy)
        IconButtonWithTooltip(onClick = { onChange(1) }, tooltip = stringResource(Res.string.top_bar_next_year)) {
            Icon(Icons.AutoMirrored.Filled.KeyboardArrowRight, contentDescription = stringResource(Res.string.top_bar_next_year), tint = DeepNavy)
        }
    }
}

@Composable
private fun PersonLeaveCard(row: TeamLeaveBalance, canEdit: Boolean, onEdit: () -> Unit) {
    val balance = row.balance
    RegularCard(modifier = Modifier.fillMaxWidth(), onClick = if (canEdit) onEdit else null) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Text(row.displayName, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = DeepNavy)
            if (balance.isKnown || balance.carryOverDays > 0) {
                Text(
                    stringResource(Res.string.leave_balance_left, balance.remainingDays, balance.totalDays),
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.Bold,
                    color = DeepNavy
                )
            }
            if (!balance.isKnown) {
                Text(stringResource(Res.string.leave_balance_unknown), style = MaterialTheme.typography.bodySmall, color = DeepNavy.copy(alpha = 0.6f))
            }
            LeaveFigures(balance)
            if (canEdit) {
                TextButton(onClick = onEdit, colors = ButtonDefaults.textButtonColors(contentColor = DarkGold)) {
                    Icon(Icons.Default.Edit, contentDescription = null, modifier = Modifier.padding(end = 4.dp))
                    Text(stringResource(Res.string.leave_balances_edit))
                }
            }
        }
    }
}

/**
 * A manager sets [row]'s entitlement (empty: from the contract) and the carry-over from last year,
 * in whole days from 0 to [LeaveAllowance.MAX_DAYS].
 */
@Composable
fun LeaveAllowanceDialog(row: TeamLeaveBalance, onDismiss: () -> Unit, onSave: (Int?, Int) -> Unit) {
    val allowance = row.entitlement.allowance
    var entitlement by remember { mutableStateOf(allowance?.entitlementDays?.toString().orEmpty()) }
    var carryOver by remember { mutableStateOf((allowance?.carryOverDays ?: 0).toString()) }
    var tried by remember { mutableStateOf(false) }

    fun parse(text: String): Int? = text.trim().toIntOrNull()?.takeIf { it in 0..LeaveAllowance.MAX_DAYS }
    val entitlementValue = entitlement.trim().takeIf { it.isNotEmpty() }?.let(::parse)
    val entitlementValid = entitlement.isBlank() || entitlementValue != null
    val carryOverValue = carryOver.trim().ifEmpty { "0" }.let(::parse)
    val invalid = stringResource(Res.string.leave_allowance_invalid, LeaveAllowance.MAX_DAYS)

    EvoDialog(
        title = stringResource(Res.string.leave_allowance_title, row.displayName),
        subtitle = row.entitlement.year.toString(),
        icon = Icons.Default.BeachAccess,
        onDismiss = onDismiss,
        confirm = DialogAction(stringResource(Res.string.save), {
            tried = true
            if (entitlementValid && carryOverValue != null) onSave(entitlementValue, carryOverValue)
        })
    ) {
        EvoTextField(
            value = entitlement,
            onValueChange = { entitlement = it.filter(Char::isDigit).take(2) },
            label = stringResource(Res.string.leave_allowance_entitlement),
            supportingText = row.entitlement.statutoryDays?.let { stringResource(Res.string.leave_allowance_entitlement_hint, it) }
                ?: stringResource(Res.string.leave_allowance_entitlement_hint_unknown),
            error = invalid.takeIf { tried && !entitlementValid },
            keyboardType = KeyboardType.Number
        )
        EvoTextField(
            value = carryOver,
            onValueChange = { carryOver = it.filter(Char::isDigit).take(2) },
            label = stringResource(Res.string.leave_allowance_carry_over),
            error = invalid.takeIf { tried && carryOverValue == null },
            keyboardType = KeyboardType.Number
        )
    }
}
